import { Resource } from "./Resource.js";
/**
 * Abstract base class for Bkper resources that support custom properties.
 *
 * Extends Resource<T> and adds property management methods for entities
 * that have a properties field in their payload.
 *
 * Custom property keys are normalized and validated by the Bkper API when
 * resources are persisted:
 *
 * - Keys can have up to 30 characters after normalization.
 * - Keys are normalized to lowercase, spaces become underscores, and unsupported
 *   punctuation is removed.
 * - Keys ending with an underscore (`_`) are treated as hidden/internal by SDK
 *   visible-property helpers.
 * - Empty, null, or undefined values clear the property when saved.
 *
 * @public
 */
export class ResourceProperty extends Resource {
    /**
     * Checks if a property key represents a hidden property.
     * Hidden properties are those whose keys end with an underscore "_".
     *
     * @param key - The property key to check
     * @returns True if the property is hidden, false otherwise
     *
     * @internal
     */
    isHiddenProperty(key) {
        return key.endsWith('_');
    }
    /**
     * Gets the custom properties stored in this resource.
     *
     * @returns Object with key/value pair properties
     */
    getProperties() {
        return this.payload.properties != null
            ? Object.assign({}, this.payload.properties) : {};
    }
    /**
     * Sets the custom properties of this resource.
     *
     * @param properties - Object with key/value pair properties
     *
     * @returns This resource, for chaining
     */
    setProperties(properties) {
        this.payload.properties = Object.assign({}, properties);
        return this;
    }
    /**
     * Gets the property value for given keys. First property found will be retrieved.
     *
     * @param keys - The property keys to search for
     *
     * @returns The property value or undefined if not found
     */
    getProperty(...keys) {
        for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            let value = this.payload.properties != null
                ? this.payload.properties[key]
                : null;
            if (value != null && value.trim() != "") {
                return value;
            }
        }
        return undefined;
    }
    /**
     * Sets a custom property in this resource.
     *
     * Property keys are normalized and validated by the API when saved. Keep keys
     * to 30 characters or fewer after normalization. Use a trailing underscore
     * (`_`) for hidden/internal properties.
     *
     * @param key - The property key
     * @param value - The property value, or null/undefined to clean it
     *
     * @returns This resource, for chaining
     */
    setProperty(key, value) {
        if (key == null || key.trim() == "") {
            return this;
        }
        if (this.payload.properties == null) {
            this.payload.properties = {};
        }
        if (!value) {
            value = "";
        }
        this.payload.properties[key] = value;
        return this;
    }
    /**
     * Deletes a custom property.
     *
     * @param key - The property key
     *
     * @returns This resource, for chaining
     */
    deleteProperty(key) {
        this.setProperty(key, null);
        return this;
    }
    /**
     * Gets the custom properties keys stored in this resource.
     *
     * @returns Array of property keys sorted alphabetically
     */
    getPropertyKeys() {
        let properties = this.getProperties();
        let propertyKeys = [];
        if (properties) {
            for (var key in properties) {
                if (Object.prototype.hasOwnProperty.call(properties, key)) {
                    propertyKeys.push(key);
                }
            }
        }
        propertyKeys = propertyKeys.sort();
        return propertyKeys;
    }
    /**
     * Sets a custom property in this resource, filtering out hidden properties.
     * Hidden properties are those whose keys end with an underscore "_".
     *
     * @param key - The property key
     * @param value - The property value, or null/undefined to clean it
     *
     * @returns This resource, for chaining
     */
    setVisibleProperty(key, value) {
        if (this.isHiddenProperty(key)) {
            return this;
        }
        return this.setProperty(key, value);
    }
    /**
     * Sets the custom properties of this resource, filtering out hidden properties.
     * Hidden properties are those whose keys end with an underscore "_".
     *
     * @param properties - Object with key/value pair properties
     *
     * @returns This resource, for chaining
     */
    setVisibleProperties(properties) {
        if (properties == null) {
            return this;
        }
        const filteredProperties = {};
        for (const key in properties) {
            if (Object.prototype.hasOwnProperty.call(properties, key)) {
                if (!this.isHiddenProperty(key)) {
                    filteredProperties[key] = properties[key];
                }
            }
        }
        return this.setProperties(filteredProperties);
    }
    /**
     * Gets the visible custom properties stored in this resource.
     * Hidden properties (those ending with "_") are excluded from the result.
     *
     * @returns Object with key/value pair properties, excluding hidden properties
     */
    getVisibleProperties() {
        const allProperties = this.getProperties();
        const visibleProperties = {};
        for (const key in allProperties) {
            if (Object.prototype.hasOwnProperty.call(allProperties, key)) {
                if (!this.isHiddenProperty(key)) {
                    visibleProperties[key] = allProperties[key];
                }
            }
        }
        return visibleProperties;
    }
}
//# sourceMappingURL=ResourceProperty.js.map